<template>
    <v-expansion-panel>
        <v-expansion-panel-header color="transparent">
        <template v-slot:actions>
            <v-icon class="icon mdi-menu-down">$expand</v-icon>
        </template>
        <template v-slot:default>
            <div class="header ml-2">
                <v-row dense>
                    <v-col cols="auto" class="text--primary">
                        {{ groupedItem.title }} ({{ groupedItem.documents.length }})
                    </v-col>
                    <v-col
                        cols="auto"
                        class="text--secondary"
                        v-for="field in sumFields"
                        v-bind:key="`sum-${field.title}`">
                        <span>
                            {{ $t("Sum") }} ({{ field.title }}) = {{ field.value }}
                        </span>
                    </v-col>
                    <v-col
                        cols="auto"
                        class="text--secondary"
                        v-for="field in maxFields"
                        v-bind:key="`max-${field.title}`">
                        <span>
                            {{ $t("MaxValue") }} ({{ field.title }}) = {{ field.value }}
                        </span>
                    </v-col>
                    <v-col
                        cols="auto"
                        class="text--secondary"
                        v-for="field in minFields"
                        v-bind:key="`min-${field.title}`">
                        <span>
                            {{ $t("MinValue") }} ({{ field.title }}) = {{ field.value }}
                        </span>
                    </v-col>
                    <v-col
                        cols="auto"
                        class="text--secondary"
                        v-for="field in avgFields"
                        v-bind:key="`avg-${field.title}`">
                        <span>
                            {{ $t("AvgValue") }} ({{ field.title }}) = {{ field.value }}
                        </span>
                    </v-col>
                </v-row>
            </div>
        </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content eager color="transparent">
            <template v-if="groupedItem.children && groupedItem.children.length > 0">
                <v-expansion-panels multiple accordion flat tile deferred>
                    <DocumentGroupTable
                        v-for="groupedItemChild in groupedItem.children"
                        v-bind:key="groupedItemChild.title"
                        :groupedItem="groupedItemChild"
                        :totalDefaultVisibleFields="totalDefaultVisibleFields"
                        :selectedDocument="selectedDocument"
                        :selectedDocuments="selectedDocuments"
                        v-on="$listeners"
                    />
                </v-expansion-panels>
            </template>
            <v-simple-table v-else class="sticky-actions-table" dense fixed-header>
                <DocumentTableHeader v-on="$listeners" :fieldList="getDefaultVisibleHeaders()" />
                <DocumentTableRow
                    v-for="document in groupedItem.documents"
                    :documentDto="document"
                    :key="document.id"
                    :headerFields="latestVersionCTFields"
                    :totalDefaultVisibleFields="totalDefaultVisibleFields"
                    :isSelected="document.ObjectID == selectedDocument"
                    :selectedDocuments="selectedDocuments"
                    :isGroupExpanded="isExpanded"
                    :isGrouped="true"
                    v-on="$listeners"
                />
            </v-simple-table>
        </v-expansion-panel-content>
    </v-expansion-panel>
</template>
<script>
import DocumentTableHeader from './DocumentTableHeader.vue';
import DocumentTableRow from './DocumentTableRow.vue';
import DocumentGroupTable from './DocumentGroupTable.vue';
import SortType from '@/ecm/variables/SortType';
import FieldType from '@/ecm/variables/FieldType';
import Utils from '@/ecm/common/Utils.js';

export default {
    components: {
        DocumentTableHeader,
        DocumentTableRow,
        DocumentGroupTable
    },
    name: 'DocumentGroupTable',
    props: {
        groupedItem: {
            type: Object,
            default: () => {}
        },
        selectedDocuments: {
            type: Array,
            default: () => [],
        },
        totalDefaultVisibleFields: {
            type: Number,
            default: 4,
        },
        selectedDocument: {
            type: Number
        }
    },
    data() {
        return {
            isExpanded: false,
            userDecSep: this.$store.getters['account/user'].MetricDecimalSep,
            userThouSep: this.$store.getters['account/user'].MetricThousandSep,
            userDateFormat: this.$store.getters['account/user'].MetricDateFormat,
        };
    },
    computed: {
        documentsToSummarize() {
            return this.groupedItem.documents.filter(
                (d) =>
                    !this.groupedItem.summarizeOnlySelectedDocuments ||
                    this.selectedDocuments.includes(d.ObjectID)
            );
        },
        sumFields() {
            return this.computeFields('sum', this.groupedItem.sumFields);
        },
        maxFields() {
            return this.computeFields('max', this.groupedItem.maxFields);
        },
        minFields() {
            return this.computeFields('min', this.groupedItem.minFields);
        },
        avgFields() {
            return this.computeFields('avg', this.groupedItem.avgFields);
        },
        latestVersionCTFields() {
            if (!this.groupedItem.documents || this.groupedItem.documents.length === 0) return [];
            const latestCt = this.groupedItem.documents.reduce((a, b) => {
                if (a.CtMajVersion > b.CtMajVersion) return a;
                if (a.CtMajVersion < b.CtMajVersion) return b;
                if (a.CtMinVersion >= b.CtMinVersion) return a;
                return b;
            });
            return (latestCt.Fields || []).filter(f => f.IsVisible).slice(0, this.totalDefaultVisibleFields);
        }
    },
    methods: {
        computeFields(type, fields) {
            if (fields.length > 0 && this.documentsToSummarize.length > 0) {
                return fields.map(f => this.summarize(type, f));
            }
            return 0;
        },
        getDefaultVisibleHeaders() {
            return this.latestVersionCTFields.map(x => ({
                code: x.Code,
                title: x.Title,
                sortOrder: SortType.Ascendent
            }));
        },
        toggleExpansion() {
            this.$nextTick(() => {
                this.isExpanded = !this.isExpanded;
            });
        },
        summarize(funcType, fieldTitle) {
            var fieldValues = this.documentsToSummarize
                .filter((gd) =>
                    gd.Fields.some((gdf) => gdf.Title == fieldTitle && gdf.Value != '')
                )
                .map((gd) => {
                    var field = gd.Fields.find((gdf) => gdf.Title == fieldTitle);
                    return this.getFieldValue(field);
                });

            var summarizedValue = '';
            if (fieldValues.length > 0) {
                switch (funcType) {
                case 'sum':
                    summarizedValue = fieldValues.reduce((a, b) => a + b);
                    break;
                case 'max':
                    summarizedValue = fieldValues.reduce((a, b) => (a > b ? a : b));
                    break;
                case 'min':
                    summarizedValue = fieldValues.reduce((a, b) => (a < b ? a : b));
                    break;
                case 'avg':
                    summarizedValue =
                        fieldValues.reduce((a, b) => a + b) / fieldValues.length;
                    break;
                }
            } else {
                summarizedValue = '0';
            }
            return {
                title: fieldTitle,
                value:
                    summarizedValue instanceof Date
                        ? Utils.date.toUserFormat(summarizedValue, this.userDateFormat)
                        : Utils.number.toUserFormat(
                            Utils.number.truncate(summarizedValue, 2),
                            this.userDecSep,
                            this.userThouSep
                        ),
            };
        },
        getFieldValue(field) {
            const standardCodes = new Set(this.$store.getters['account/searchStandardFields'].map(sf => sf.Code));
            let dateOnly;
            switch (field.Type) {
            case FieldType.Numeric:
                return Utils.number.fromUserFormat(field.Value, this.userDecSep, this.userThouSep);
            default:
                if (standardCodes.has(field.Code))
                    dateOnly = field.Value.split(' ')[0];
                else
                    dateOnly = field.Value;
                return Utils.date.fromUserFormat(dateOnly, this.userDateFormat);
            }
        }
    }
};
</script>